import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { compose } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { BettingHistoryRouteType, BettingHistoryType, CashOutServerType, SendCashOutResponse, CashoutAbleTabType } from '@om-fe/api';
import { BettingHistoryDetailsParams, WidgetsRouteChangeDetails, WidgetsRouteName } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { BettingHistoryItemV2ClassModifiers } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item/v2/betting-history-item-v2.utils';
import { BettingHistoryItemLayout } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item.layout';
import { BettingHistoryList } from '@om-fe/widgets/dist/widgets/betting-history/list/betting-history-list';
import { useI18n } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import { SettledBetFilterType } from '@om-fe/widgets/dist/widgets/betting-history/list/betting-history-list.utils';
import { RangePickerValue } from '@om-fe/widgets/dist/components/range-picker/rangepicker';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { EMRoutingContext, EMRoutingContextInterface, RouteNames } from '@om-fe/core/core/routing/utils';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { AppState } from '@om-fe/core/core/types';
import { LoginPayload, OddsFormat, ReceivedMessageType, listenToMessageWithoutWarning, PlatformTypes, SendMessageType, sendMessageWithoutWarning } from '@om-fe/utils';
import { BettingHistoryStatusHeader, BettingHistoryWithLiveStatusHeader, BettingHistoryCashoutAbleHeader, BettingHistoryOpenBetsV2WithLiveStatusHeader } from '@om-fe/core/components/betting-history/betting-history-status-header';
import getConfig, { BetslipConfig, DateFormatConfigKey, getConfigDateFormatType, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { UserAuthMethod } from '@om-fe/core/utils/user';
import { SettledBetsFilters } from '../components/betslip-wrapper/settled-bets-filters/settled-bets-filters';
import dayjs from 'dayjs';
import { IsAuthenticatedStatus, useAuthenticatedStatus } from '@om-fe/store';
import { BetStatus } from '@om-fe/models';
import { scrollContainerToTop } from '@om-fe/core/utils/dom';
import { getVirtualState, setVirtualState, VirtualStateType, VirtualStateModel } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import { BetslipHistoryVirtualTabs, BetslipVirtualBetsTabs } from '../components/betslip-wrapper/betslip-history-virtual-tabs';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import { BettingHistoryComponentStatus } from '@om-fe/core/components/betting-history/api/betting-history/betting-history.constants';

type BettingHistoryPageRouteProps = RouteComponentProps<{ readonly page?: string, readonly status?: string }>;

const FETCH_SESSION_ID_MESSAGE = 'OMFE:fetchSessionID' as SendMessageType;

interface BettingHistoryPageStoreProps {
    breakpoints: BreakpointsState;
    oddsFormat: OddsFormat;
    deviceType?: PlatformTypes;
}

type BettingHistoryPageProps = PropsWithChildren<BettingHistoryPageRouteProps & BettingHistoryPageStoreProps>;

function BettingHistoryComponent(props: BettingHistoryPageProps) {
    const authStatus: IsAuthenticatedStatus = useAuthenticatedStatus();
    const { getPath, getRouteByUrl } = useContext<EMRoutingContextInterface>(EMRoutingContext);
    const { gettext } = useI18n();
    const { getFullPath } = useEMRouting();
    const {
        match: { params: { page, status } },
        history,
        breakpoints: { small },
        oddsFormat,
        children,
        deviceType,
    } = props;

    const config: OMFE2ConfigModel = getConfig();
    const betslip: BetslipConfig = config.betslip;
    const [showDetailsForBet, setShowBetId] = useState<string | undefined>();
    const [updateCounter, setUpdateCounter] = useState<number>(1);
    const [openBetsCount, setOpenBetsCount] = useState<number | undefined>(undefined);
    const [openLiveBetsCount, setOpenLiveBetsCount] = useState<number>(0);
    const [filterType, setFilterType] = useState<SettledBetFilterType>(SettledBetFilterType.PlacementDate);
    const [filterStatus, setFilterStatus] = useState<BetStatus | undefined>(undefined);
    const [searchStatus, setSearchStatus] = useState<BetStatus | undefined>(undefined);
    const [showSettledBetsFiltersModal, setShowSettledBetsFiltersModal] = useState<boolean>(false);
    const calendarDateLimit: number = betslip?.numberOfDaysLimitSettleBetFilters || (betslip?.disabledLimitSettleBetFilters ? 1080 : 90);
    const currentDate: Date = dayjs().startOf('date').toDate();
    const defaultStartDate: Date = dayjs(currentDate).subtract(calendarDateLimit - 1, 'day').toDate();
    const maxStartDate: Date = dayjs().endOf('date').toDate();
    const [filterStartDate, setFilterStartDate] = useState<string | undefined>(betslip?.disabledSettleBetFiltersModal ? dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
    const [filterEndDate, setFilterEndDate] = useState<string | undefined>(betslip?.disabledSettleBetFiltersModal ? dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
    const [currentFilterPage, setCurrentFilterPage] = useState<number>(1);
    const [selectedCashoutAbleTab, setSelectedCashoutAbleTab] = useState<CashoutAbleTabType>(CashoutAbleTabType.all);
    const [redirectStatus, setRedirect] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const loggedOutMessagePostedRef = useRef<boolean>(false);
    const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const fetchSessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const userAuthMethod: UserAuthMethod = config.userAuthMethod;
    const bettingHistoryLiveTab: boolean | undefined = config.bettingHistoryLiveTab;
    const showWinTaxPercentage: boolean = config.bettingHistory?.showWinTaxPercentage ?? false;
    const taxPercentage: number | undefined = betslip?.taxPercentage;
    const showMarketnameOnly: boolean | undefined = betslip?.showMarketnameOnly;
    const enabledCashoutAble: boolean = betslip?.enabledCashoutAbleFilter ?? false;
    const enabledSettledBetV2: boolean = betslip?.enabledSettledBetV2 ?? false;
    const enabledRebet: boolean = betslip?.enabledRebet ?? false;
    const disabledSettleBetFiltersModal: boolean = betslip?.disabledSettleBetFiltersModal ?? false;
    const virtualTabConfig: boolean = betslip?.enabledVirtualHistoryTabs ?? false;
    const [virtualTabs, setVirtualTabs] = useState<BetslipVirtualBetsTabs>(BetslipVirtualBetsTabs.AllBets);
    const betsDetailsSortable: boolean = betslip?.betsDetailsSortable ?? false;
    const hasSimulateBetButton: boolean = !!config?.simulate?.showSimulateBetButton;
    const enabledVirtualTab: boolean = virtualTabConfig || hasSimulateBetButton;

    const [filterDateRange, setDateRange] = useState<RangePickerValue>(() => ({
        start: dayjs(defaultStartDate).toDate().getTime(),
        end: dayjs(maxStartDate).toDate().getTime(),
    }));

    const currentPage = useMemo<number>(() => {
        return parseInt(page);
    }, [page]);

    const handlePageChange = useCallback((newPage: number) => {
        if (filterType === SettledBetFilterType.None) {
            const virtualState: VirtualStateModel[] = getVirtualState();
            const path: string = getPath(RouteNames.bettingHistory, { page: newPage, status });
            if (config.virtualState) {
                const state = setVirtualState(VirtualStateType.forwardState, virtualState, path, getRouteByUrl(path));
                history.push(path, state);
                setVirtualState(VirtualStateType.newState, virtualState, path, getRouteByUrl(path));
            } else {
                history.push(path);
            }
        } else {
            setCurrentFilterPage(newPage);
        }

    }, [history, status, filterType]);

    const handleOpenSettledBetsFilters = useCallback(() => {
        setShowSettledBetsFiltersModal(true);

    }, []);

    const handleCloseSettledBetsFilters = useCallback(() => {
        if (filterType === SettledBetFilterType.None) {
            setFilterType(SettledBetFilterType.PlacementDate);
        }
        setCurrentFilterPage(1);
        setShowSettledBetsFiltersModal(false);
        setFilterStartDate(filterDateRange.start ? dayjs(filterDateRange.start).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
        setFilterEndDate(filterDateRange.end ? dayjs(filterDateRange.end).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
        setSearchStatus(filterStatus);
    }, [filterType, filterDateRange, filterStatus]);

    const handleFilterChange = useCallback((tab: SettledBetFilterType) => {
        if (tab === SettledBetFilterType.PlacementDate) {
            setFilterType(tab);
        }
    }, []);

    const handleFilterStatusChange = useCallback((status: BetStatus | undefined) => {
        setFilterStatus(status);
        if (disabledSettleBetFiltersModal) {
            setSearchStatus(status);
        }
    }, [disabledSettleBetFiltersModal]);

    const handleDateRangeClear = useCallback(() => {
        setFilterStartDate(dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss'));
        setFilterEndDate(dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss'));
        setDateRange({ start: dayjs(defaultStartDate).toDate().getTime(), end: dayjs(maxStartDate).toDate().getTime() });
    }, [defaultStartDate, maxStartDate]);

    const handleDateRangeChange = useCallback((range: RangePickerValue) => {
        setDateRange({ start: range.start, end: range.end ? range.end : range.start });
    }, []);

    const handleRouteChange = useCallback(
        (details: WidgetsRouteChangeDetails<WidgetsRouteName.Tournament | WidgetsRouteName.Match | WidgetsRouteName.BettingHistoryDetails>) => {
            if (details.name === WidgetsRouteName.BettingHistoryDetails) {
                setShowBetId((details.params as BettingHistoryDetailsParams).betId);
            }
        },
        [],
    );

    const handleAfterCashout = (response: SendCashOutResponse) => {
        if (response?.success) {
            setTimeout(() => {
                setUpdateCounter(counter => {
                    return ++counter;
                })
            }, 7000);
        }
    };

    const handleCashoutTab = useCallback((status: CashoutAbleTabType) => {
        setCurrentFilterPage(1);
        setSelectedCashoutAbleTab(status);
    }, []);

    useEffect(() => {
        scrollContainerToTop();
    }, [currentFilterPage]);

    useEffect(() => {
        if (status === BettingHistoryRouteType.Settled) {
            setFilterType(SettledBetFilterType.PlacementDate);
        } else {
            setFilterType(SettledBetFilterType.None);
            setDateRange({
                start: dayjs(defaultStartDate).toDate().getTime(),
                end: dayjs(maxStartDate).toDate().getTime(),
            });
            setSelectedCashoutAbleTab(CashoutAbleTabType.all);
        }
        setCurrentFilterPage(1);

        return () => {
            if (status === BettingHistoryRouteType.Settled && enabledSettledBetV2) {
                setFilterStatus(undefined);
                setSearchStatus(undefined);
                setFilterStartDate(betslip?.disabledSettleBetFiltersModal ? dayjs(defaultStartDate).startOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
                setFilterEndDate(betslip?.disabledSettleBetFiltersModal ? dayjs(maxStartDate).endOf('date').format('YYYY-MM-DDTHH:mm:ss') : undefined);
            }
        }
    }, [status]);

    const openBetsUpdateCounter = useCallback((counter: number, liveCounter: number) => {
        setOpenBetsCount(counter);
        setOpenLiveBetsCount(liveCounter);
    }, []);

    const redirectToHome = useCallback(() => {
        setRedirect(true);
    }, []);

    const getBettingHistoryType = useCallback((status: string | undefined): BettingHistoryType => {
        switch(status) {
            case BettingHistoryRouteType.Live:
                return BettingHistoryType.liveBets;
            case BettingHistoryRouteType.Open:
                return BettingHistoryType.openBets;
            case BettingHistoryRouteType.Settled:
                return BettingHistoryType.settledBets;
            default:
                return BettingHistoryType.openBets;
        }
    }, []);

    const [allBetExpanded, setAllBetExpanded] = useState<boolean>(false);

    const handleToggle = useCallback(() => {
        setAllBetExpanded(current => !current);
        setShowBetId('');
    }, []);

    useEffect(() => {
        let loginUnsubscribe: () => void;
        loginUnsubscribe = listenToMessageWithoutWarning(ReceivedMessageType.Login, (payload: LoginPayload) => {
            if (payload.currentSession) {
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = undefined;
                }
            }
        });

        return () => {
            loginUnsubscribe();
        }
    }, []);

    useLayoutEffect(() => {
        if (authStatus === IsAuthenticatedStatus.Loading || authStatus === IsAuthenticatedStatus.NotLoggedIn) {
            setIsLoading(true);
            loadingTimeoutRef.current = setTimeout(() => {
                setIsLoading(false);
                loadingTimeoutRef.current = undefined;
            }, 7000);
        } else if (authStatus === IsAuthenticatedStatus.IsLoggedIn) {
            setIsLoading(false);
        }

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = undefined;
            }
        };
    }, [authStatus]);

    const shouldShowLoggedOutMessage: boolean = !isLoading
        && userAuthMethod === UserAuthMethod.CESession
        && authStatus !== IsAuthenticatedStatus.IsLoggedIn;

    useEffect(() => {
        if (!shouldShowLoggedOutMessage) {
            loggedOutMessagePostedRef.current = false;
            if (fetchSessionTimeoutRef.current) {
                clearTimeout(fetchSessionTimeoutRef.current);
                fetchSessionTimeoutRef.current = undefined;
            }
            return;
        }

        if (loggedOutMessagePostedRef.current) {
            return;
        }

        fetchSessionTimeoutRef.current = setTimeout(() => {
            sendMessageWithoutWarning({
                type: FETCH_SESSION_ID_MESSAGE,
            });
            fetchSessionTimeoutRef.current = undefined;
            loggedOutMessagePostedRef.current = true;
        }, 300);

        return () => {
            if (fetchSessionTimeoutRef.current) {
                clearTimeout(fetchSessionTimeoutRef.current);
                fetchSessionTimeoutRef.current = undefined;
            }
        };
    }, [shouldShowLoggedOutMessage]);

    const handleVirtualTabChange = useCallback((tab: BetslipVirtualBetsTabs) => {
        setVirtualTabs(tab);
    }, [])

    if (redirectStatus) {
        return (
            <RedirectTo routeName={RouteNames.home} />
        );
    }

    const {
        desktop: showOnDesktop = false,
        tablet: showOnTablet = false,
        mobile: showOnMobile = false,
    } = betslip?.shareBetButtonOnMyBets ?? {};

    const isDesktop: boolean = deviceType === 'desktop';
    const isTablet: boolean = deviceType === 'tablet';
    const isMobile: boolean = deviceType === 'mobile';

    const showShareBet: boolean = (showOnDesktop && isDesktop) || (showOnTablet && isTablet) || (showOnMobile && isMobile);

    return (
        <>
            <h1 className={'PageTitle'}>
                {gettext('Betting history')}
            </h1>
            {enabledVirtualTab && (
                <BetslipHistoryVirtualTabs
                    selectedTab={virtualTabs}
                    onTabClick={handleVirtualTabChange}
                    hasVirtualBets={virtualTabConfig}
                    hasSimulateBetsV2={hasSimulateBetButton}
                    routeSimulateBetsV2={hasSimulateBetButton ? getFullPath(RouteNames.bettingHistory, { page: 1, status: BettingHistoryComponentStatus.settled }) : undefined}
                />
            )}
            {children}
            {authStatus === IsAuthenticatedStatus.IsLoggedIn && virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2 && (bettingHistoryLiveTab
                ?
                    enabledCashoutAble
                        ?
                            <BettingHistoryOpenBetsV2WithLiveStatusHeader
                                status={getBettingHistoryType(status)}
                                openBetsCount={openBetsCount}
                                liveBetsCount={openLiveBetsCount}
                            />
                        :
                            <BettingHistoryWithLiveStatusHeader
                                status={getBettingHistoryType(status)}
                                updateCounter={updateCounter}
                            />
                : <BettingHistoryStatusHeader status={getBettingHistoryType(status)} />
            )}
            {
                authStatus === IsAuthenticatedStatus.IsLoggedIn &&
                betslip?.settledBetsFilters &&
                getBettingHistoryType(status) === BettingHistoryType.settledBets &&
                virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2 && (
                    <SettledBetsFilters
                        tabActive={filterType === SettledBetFilterType.None ? SettledBetFilterType.PlacementDate : filterType}
                        statusActive={filterStatus}
                        onTabChanged={handleFilterChange}
                        onStatusChanged={handleFilterStatusChange}
                        onClose={handleCloseSettledBetsFilters}
                        onOpen={handleOpenSettledBetsFilters}
                        opened={showSettledBetsFiltersModal}
                        minStartDate={defaultStartDate}
                        filterDateRange={filterDateRange}
                        maxStartDate={maxStartDate}
                        handleDateRangeChange={handleDateRangeChange}
                        useModal={disabledSettleBetFiltersModal ? false : true}
                        allBetExpanded={allBetExpanded}
                        handleToggle={handleToggle}
                        handleDateRangeClear={handleDateRangeClear}
                        deviceType={deviceType}
                        disabledAllBetExpandButton={disabledSettleBetFiltersModal}
                    />
                )
            }
            {authStatus === IsAuthenticatedStatus.IsLoggedIn && !!(enabledCashoutAble && getBettingHistoryType(status) !== BettingHistoryType.settledBets && virtualTabs !== BetslipVirtualBetsTabs.SimulateBetsV2) &&
                <BettingHistoryCashoutAbleHeader
                    onStatusChanged={handleCashoutTab}
                    status={selectedCashoutAbleTab}
                />
            }
            {authStatus === IsAuthenticatedStatus.IsLoggedIn || userAuthMethod !== UserAuthMethod.CESession
                ?
                    <BettingHistoryList
                        itemLayout={BettingHistoryItemLayout.V3}
                        classModifier={small ? BettingHistoryItemV2ClassModifiers.Small : BettingHistoryItemV2ClassModifiers.Big}
                        className={`OM-BettingHistory--${getBettingHistoryType(status)}`}
                        betStatus={getBettingHistoryType(status)}
                        nrOfRecords={20}
                        onPageChange={handlePageChange}
                        page={currentPage}
                        filterPage={currentFilterPage}
                        oddsFormat={oddsFormat}
                        onRouteChange={handleRouteChange}
                        bettingHistoryDetailsRoute={false}
                        dateFormat={getConfigDateFormatType(DateFormatConfigKey.FormatFullDate)}
                        cashoutServer={CashOutServerType.v2}
                        hasShareBet={showShareBet}
                        betIdExpanded={showDetailsForBet}
                        showDateSeparator={status === BettingHistoryRouteType.Settled ? true : false}
                        onAfterCashout={handleAfterCashout}
                        showCashoutPending={config.cashoutPending}
                        filterType={getBettingHistoryType(status) === BettingHistoryType.settledBets ? filterType : undefined}
                        searchStatus={getBettingHistoryType(status) === BettingHistoryType.settledBets ? searchStatus: undefined}
                        startDate={getBettingHistoryType(status) === BettingHistoryType.settledBets ? filterStartDate : undefined}
                        endDate={getBettingHistoryType(status) === BettingHistoryType.settledBets ? filterEndDate : undefined}
                        taxPercentage={taxPercentage}
                        enabledDateOnMultiple={betslip?.enabledDateOnMultiple}
                        showMarketnameOnly={showMarketnameOnly}
                        pinIcon={!!(betslip?.systemBetV2?.pinIcon)}
                        matchDateFormat={config.dateFormat?.FormatDay}
                        enabledCashoutAble={enabledCashoutAble}
                        enabledSettledBetV2={enabledSettledBetV2}
                        showCashoutOnly={selectedCashoutAbleTab === CashoutAbleTabType.cashoutAble ? true : false}
                        openBetsUpdateCounter={openBetsUpdateCounter}
                        enabledRebet={enabledRebet}
                        redirectToHome={small ? undefined : redirectToHome}
                        allBetExpanded={disabledSettleBetFiltersModal ? false : allBetExpanded}
                        showWinTaxPercentage={showWinTaxPercentage}
                        virtualTabs={enabledVirtualTab? virtualTabs : undefined}
                        betsDetailsSortable={betsDetailsSortable}
                    />
                : isLoading && userAuthMethod === UserAuthMethod.CESession
                    ? <Spinner />
                    : <div className="LoggedOutMessage">{gettext('You must be logged in to access this page.')}</div>
            }
        </>
    );
}

export const BettingHistoryPage = compose(
    withRouter,
    connect<BettingHistoryPageStoreProps, null, {}, AppState>(
        (state: AppState): BettingHistoryPageStoreProps => {
            return {
                breakpoints: state.breakpoints,
                oddsFormat: state.settings.oddsFormat,
                deviceType: state.deviceInfo?.platform.type,
            };
        },
    ),
)(BettingHistoryComponent);
